/**
 * TaskService — the WORK space's task collection (`teacherTasks`).
 *
 * The implementation moved to taskServiceFactory.js when tasks gained a second
 * space; this file is now just the Work binding. The public API is UNCHANGED —
 * same named exports, same default-export subset, same behaviour — so all five
 * importers (WorkTasksModule, TaskForm, TaskDetail, TeacherTasks,
 * LessonDashboard) are untouched by the split.
 *
 * Tasks are linked to students and visible only to staff: admins/owners see all,
 * an employee with manageTasks sees and edits only tasks assigned to her
 * (assigneeUids). The Personal space's equivalent is personalTaskService.js,
 * bound to a separate owner-walled collection rather than to a `space` field on
 * this one.
 *
 * `taskChangeState` is re-exported straight from the factory module: it is a
 * pure helper that touches no collection, so it is shared across bindings rather
 * than rebuilt per service. (taskChangeState.test.js imports it from here.)
 */

import { createTaskService, taskChangeState } from './taskServiceFactory';

const TaskService = createTaskService({
	tasksCollection: 'teacherTasks',
	space: 'work',
	categoriesCollection: 'taskCategories',
});

export { taskChangeState };

/* The COMPLETE factory instance — every function plus the introspection fields
   (tasksCollection, space, categoriesCollection). Distinct from the default
   export below, which is deliberately a 23-key subset for backward
   compatibility. TaskServiceContext uses this one: a partial object would give
   the Work default a different shape from every other binding, which is exactly
   the asymmetry that makes a "defaults to Work" guarantee untrustworthy. */
export { TaskService as workTaskService };

export const {
	subscribeToMyTaskReads,
	markTaskRead,
	createTask,
	updateTask,
	updateTaskStatus,
	deleteTask,
	getTasksByStudent,
	getIncompleteTasks,
	getTasksByTeacher,
	subscribeToStudentTasks,
	subscribeToIncompleteTasks,
	subscribeToAllTasks,
	subscribeToAssignedTasks,
	subscribeToVisibleTasks,
	addTaskComment,
	deleteTaskComment,
	subscribeToTaskComments,
	createSubtask,
	updateSubtask,
	updateSubtaskStatus,
	deleteSubtask,
	subscribeToSubtasks,
	createCategory,
	getCategories,
	subscribeToCategories,
	deleteCategory,
} = TaskService;

/* The default export deliberately omits subscribeToMyTaskReads, markTaskRead and
   taskChangeState — it always has. Those three are named-export only, and the
   shape is preserved verbatim so the split can't quietly change what a
   `import TaskService from` consumer sees. */
export default {
	createTask,
	updateTask,
	updateTaskStatus,
	deleteTask,
	getTasksByStudent,
	getIncompleteTasks,
	getTasksByTeacher,
	subscribeToStudentTasks,
	subscribeToIncompleteTasks,
	subscribeToAllTasks,
	subscribeToAssignedTasks,
	addTaskComment,
	deleteTaskComment,
	subscribeToTaskComments,
	// Subtasks
	createSubtask,
	updateSubtask,
	updateSubtaskStatus,
	deleteSubtask,
	subscribeToSubtasks,
	// Categories
	createCategory,
	getCategories,
	subscribeToCategories,
	deleteCategory,
};
